"use client";

import { Mail, Instagram, Linkedin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-3">
        <li className="h-5 w-24 bg-muted/40 animate-pulse rounded-md" />
        <li className="h-5 w-20 bg-muted/40 animate-pulse rounded-md" />
        <li className="h-5 w-28 bg-muted/40 animate-pulse rounded-md" />
      </ul>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li data-index={i} key={item.id}>
          <Link to={item.href || "#"} className="font-default text-base text-white transition-all duration-300 hover:text-blue-300">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
const studioLinks = [{
  label: "Builder Directory",
  to: "/HomePage"
}, {
  label: "Our Methodology",
  to: "/HomePage"
}, {
  label: "Consultation",
  to: "/HomePage"
}];
const officeLinks = [{
  label: "Columbus",
  to: "#"
}, {
  label: "Cleveland",
  to: "#"
}, {
  label: "Cincinnati",
  to: "#"
}];
const legalLinks = [{
  label: "Privacy",
  to: "#"
}, {
  label: "Terms",
  to: "#"
}, {
  label: "Licensing",
  to: "#"
}];
export default function Footer() {
  const {
    ref: revealRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="4723" id="footer" className="dark bg-blue-600 text-white relative overflow-hidden">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 -left-8 select-none font-default text-[20rem] leading-none text-white/10 md:text-[28rem]">
        IQ
      </div>

      <div ref={revealRef} className={`relative mx-auto max-w-7xl px-6 pt-24 pb-12 transition-all duration-1000 ease-out md:px-12 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {}
        <div className="absolute inset-x-6 top-0 h-px bg-white/20 md:inset-x-12" />

        {}
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-8">
          {}
          <div className="md:col-span-5">
            <Link to="/" className="inline-block">
              <h2 className="font-default font-bold text-6xl leading-none tracking-tight text-white md:text-7xl">
                BUILDIQ
              </h2>
            </Link>
            <p className="mt-6 font-default text-base text-blue-100">
              Central Ohio's premier home builder resource.
            </p>
            <address className="mt-8 not-italic text-xs uppercase tracking-[0.2em] text-blue-200">
              100 Innovation Drive
              <br />
              Columbus &middot; Cleveland &middot; Cincinnati
            </address>
          </div>

          {}
          <div className="md:col-span-2 md:col-start-7">
            <h3 className="font-default text-xs uppercase tracking-[0.2em] text-blue-200">
              Resources
            </h3>
            <Separator className="my-4 bg-white/20" />
            <MenuProvider menu_id="footer_menu">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="md:col-span-2">
            <h3 className="font-default text-xs uppercase tracking-[0.2em] text-blue-200">
              Company
            </h3>
            <Separator className="my-4 bg-white/20" />
            <ul className="space-y-3">
              {studioLinks.map((link, i) => <li data-index={i} key={link.label}>
                  <Link to={link.to} className="font-default text-base text-white transition-all duration-300 hover:text-blue-300">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </div>

          {}
          <div className="md:col-span-2">
            <h3 className="font-default text-xs uppercase tracking-[0.2em] text-blue-200">
              Locations
            </h3>
            <Separator className="my-4 bg-white/20" />
            <ul className="space-y-3">
              {officeLinks.map((link, i) => <li data-index={i} key={link.label}>
                  <Link to={link.to} className="font-default text-base text-white transition-all duration-300 hover:text-blue-300">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </div>
        </div>

        {}
        <Separator className="mt-20 mb-8 bg-white/20" />

        {}
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
          {}
          <p className="font-default text-xs text-blue-200">
            &copy; 2025 BUILDIQ. All rights reserved.
          </p>

          {}
          <div className="flex items-center gap-6">
            <Link to="#" newTab aria-label="Instagram" className="text-white transition-colors duration-300 hover:text-blue-300">
              <Instagram className="size-4" strokeWidth={1} />
            </Link>
            <Link to="#" newTab aria-label="LinkedIn" className="text-white transition-colors duration-300 hover:text-blue-300">
              <Linkedin className="size-4" strokeWidth={1} />
            </Link>
            <Link to="mailto:hello@buildiq.com" aria-label="Email" className="text-white transition-colors duration-300 hover:text-blue-300">
              <Mail className="size-4" strokeWidth={1} />
            </Link>
          </div>

          {}
          <div className="flex items-center">
            {legalLinks.map((link, i) => <div data-index={i} key={link.label} className="flex items-center">
                <Link to={link.to} className="font-default text-xs text-blue-200 transition-colors duration-300 hover:text-white">
                  {link.label}
                </Link>
                {i < legalLinks.length - 1 && <span aria-hidden="true" className="mx-3 inline-block h-3 w-px bg-white/20" />}
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}